import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { fetchUserProfile } from "@/features/auth/services/auth.service";
import type { UserProfile } from "@/types/domain";
import { queryKeys } from "@/lib/queryKeys";
import { logErrorToConsole } from "@/utils/errorHandlerUtils";

export const useUserProfileData = () => {
  const {
    data: userProfile,
    isLoading,
    error,
    isError,
  } = useQuery({
    queryKey: queryKeys.userProfile(),
    queryFn: async (): Promise<UserProfile | null> => {
      const response = await fetchUserProfile();
      if (response.status === 'error') {
        throw new Error(response.error?.message || 'No se pudo cargar el perfil del usuario.');
      }
      return response.data ?? null;
    },
    staleTime: 0,
    // El perfil es el guard de toda la aplicación. Al abrir una pestaña nueva
    // se confirma contra RLS aunque exista una copia offline; evita mostrar por
    // error el estado "sin acceso" de una hidratación anterior.
    // Varios componentes montan este hook en cascada al abrir una ruta; con
    // "always" a secas cada montaje repetía la petición. Se confirma siempre
    // salvo que la copia tenga menos de 5 s (acaba de llegar de la red).
    refetchOnMount: (query) =>
      Date.now() - query.state.dataUpdatedAt < 5_000 ? false : "always",
    refetchOnWindowFocus: "always",
    refetchOnReconnect: "always",
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
  });

  const isUserActive = userProfile?.is_active ?? false;

  // Se registra una vez por error, no en cada render de cada componente que
  // monta el hook.
  useEffect(() => {
    if (error) {
      logErrorToConsole(error, { module: 'useUserProfileData', operation: 'fetchUserProfile' });
    }
  }, [error]);

  return {
    userProfile: userProfile ?? null,
    isUserActive,
    isLoading,
    isError,
    error,
  };
};
